import { ReactNode, useMemo } from 'react';
import { Api } from 'vopay';
import { ApiProvider } from 'shared';
import { api } from '@/services/api.config';

type PartnerScopedApiProviderProps = {
  /** The partner account's own JWT, as `/partner/partners` returns it with `ShowToken`. */
  bearer: string;
  children: ReactNode;
};

/**
 * Re-binds, to one partner account's bearer, the api methods the shared features mounted on the partner
 * page call — the partner routes they speak authenticate as the partner account, not the parent's
 * session (the Account Transactions tab: `/partner/account/transactions` for its rows, ID-13796, and
 * `/partner/account` for its Account filter, ID-14075). Each method keeps the api's own optional `bearer`
 * parameter, so a call that passes its own still wins, and every method not listed resolves the parent's
 * session as before. Shared never learns which host it is in.
 */
export default function PartnerScopedApiProvider({ bearer, children }: PartnerScopedApiProviderProps) {
  const scopedApi = useMemo<Api>(
    () => ({
      ...api,
      fetchPartnerAccountTransactions: (params, source, requestBearer) =>
        api.fetchPartnerAccountTransactions(params, source, requestBearer ?? bearer),
      fetchPartnerAccounts: (params, source, requestBearer) =>
        api.fetchPartnerAccounts(params, source, requestBearer ?? bearer),
    }),
    [bearer],
  );

  return <ApiProvider api={scopedApi}>{children}</ApiProvider>;
}
